import React, { useCallback, useEffect } from 'react';
import { User, MicOff, Monitor, Hand } from 'lucide-react';
import { Participant, VideoBackgroundEffect } from '../types/meeting.types';
import { attachStreamToVideo } from '../utils/mediaUtils.ts';

interface VideoTileProps {
  participant: Participant;
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  isLocalVideo?: boolean;
  isVideoEnabled?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
  fullScreen?: boolean;
  size?: 'small' | 'normal' | 'large';
  showCameraInsteadOfScreen?: boolean;
  preferContain?: boolean;
  forceLandscapeFrame?: boolean;
  displayStream?: MediaStream | null;
  videoBackgroundEffect?: VideoBackgroundEffect;
  transparentMode?: boolean;
  backgroundProcessed?: boolean;
}

function getBackgroundClass(effect: VideoBackgroundEffect): string {
  switch (effect) {
    case 'blur':
      return 'bg-slate-600';
    case 'purple':
      return 'bg-gradient-to-br from-purple-800 to-slate-900';
    case 'blue':
      return 'bg-gradient-to-br from-blue-800 to-slate-900';
    case 'transparent':
      return 'bg-transparent';
    default:
      return 'bg-slate-800';
  }
}

const VideoTile: React.FC<VideoTileProps> = ({
  participant,
  videoRef,
  isLocalVideo = false,
  isVideoEnabled = true,
  isSelected = false,
  onClick,
  fullScreen = false,
  size = 'normal',
  showCameraInsteadOfScreen = false,
  preferContain = false,
  forceLandscapeFrame = false,
  displayStream = null,
  videoBackgroundEffect = 'none',
  transparentMode = false,
  backgroundProcessed = false,
}) => {
  const activeStream = displayStream ?? participant.stream ?? null;
  // Uzak katılımcı kamerasını kapattıysa track siyah kare üretir — video yerine
  // avatar gösterilmeli. Ekran paylaşımı ise kamera durumundan bağımsız gösterilir.
  const remoteVideoVisible =
    !!activeStream && (participant.isScreenSharing || participant.isVideoEnabled !== false);
  const showVideo = isLocalVideo
    ? (isVideoEnabled && !!activeStream)
    : (showCameraInsteadOfScreen ? false : remoteVideoVisible);
  const showPlaceholder = !showVideo;
  const isLarge = size === 'large';
  const objectFitClass =
    isLarge || preferContain || backgroundProcessed ? 'object-contain' : 'object-cover';
  const frameClass =
    isLarge ? 'w-full h-full' : forceLandscapeFrame ? 'aspect-video w-full' : 'w-full h-full';
  const useBlurBg =
    isLocalVideo && videoBackgroundEffect === 'blur' && showVideo && !backgroundProcessed;
  const bgClass = backgroundProcessed
    ? 'bg-black'
    : isLocalVideo
      ? getBackgroundClass(videoBackgroundEffect)
      : 'bg-slate-800';

  const shouldMirror =
    isLocalVideo &&
    !participant.isScreenSharing &&
    !showCameraInsteadOfScreen &&
    !backgroundProcessed;
  const nameClass = participant.handRaised
    ? 'text-amber-300 font-semibold'
    : 'text-white font-medium';

  const bindVideoRef = useCallback((node: HTMLVideoElement | null) => {
    if (videoRef && 'current' in videoRef) {
      videoRef.current = node;
    }
    if (!node || !activeStream) return;
    if (isLocalVideo && !isVideoEnabled) return;
    if (!isLocalVideo && showCameraInsteadOfScreen) return;
    attachStreamToVideo(node, activeStream);
  }, [activeStream, isLocalVideo, isVideoEnabled, showCameraInsteadOfScreen, videoRef]);

  const bindBlurVideoRef = useCallback((node: HTMLVideoElement | null) => {
    if (!node || !activeStream || !useBlurBg) return;
    attachStreamToVideo(node, activeStream);
  }, [activeStream, useBlurBg]);

  useEffect(() => {
    const node = videoRef?.current;
    if (!node || !activeStream) return;
    if (isLocalVideo && !isVideoEnabled) return;
    if (!isLocalVideo && showCameraInsteadOfScreen) return;
    attachStreamToVideo(node, activeStream);
  }, [activeStream, isLocalVideo, isVideoEnabled, showCameraInsteadOfScreen, videoRef]);

  const videoProps = {
    ref: bindVideoRef,
    'data-socket-id': participant.id,
    autoPlay: true as const,
    playsInline: true as const,
    muted: isLocalVideo,
    className: `w-full h-full ${objectFitClass} ${showVideo ? 'block relative z-10' : 'hidden'} ${
      transparentMode && isLocalVideo ? 'opacity-90' : ''
    }`,
    style: { transform: shouldMirror ? 'scaleX(-1)' : 'none' } as React.CSSProperties,
  };

  const emojiBadge = participant.activeEmoji ? (
    <div className="absolute top-2 left-2 sm:top-3 sm:left-3 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center text-xl sm:text-2xl z-20 pointer-events-none">
      {participant.activeEmoji}
    </div>
  ) : null;

  const micBadge = !participant.isAudioEnabled ? (
    <div className="absolute top-2 right-2 sm:top-3 sm:right-3 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center z-20 pointer-events-none">
      <MicOff className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
    </div>
  ) : null;

  const nameLabel = (
    <div className="flex items-center gap-1 min-w-0">
      {participant.handRaised && (
        <Hand className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
      )}
      <span className={`truncate ${nameClass}`}>
        {participant.name} {participant.isCurrentUser && '(Siz)'}
      </span>
    </div>
  );

  const videoLayer = (
    <>
      {useBlurBg && (
        <video
          ref={bindBlurVideoRef}
          autoPlay
          playsInline
          muted
          className={`absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-70 ${shouldMirror ? 'scale-x-[-1]' : ''}`}
        />
      )}
      <video {...videoProps} />
    </>
  );

  const recordingBg =
    backgroundProcessed || videoBackgroundEffect === 'none'
      ? 'default'
      : videoBackgroundEffect;
  const recordingTileProps = {
    'data-recording-tile': '',
    'data-recording-bg': recordingBg,
  } as const;

  if (fullScreen) {
    return (
      <div className={`relative bg-black ${frameClass}`} {...recordingTileProps}>
        {videoLayer}
        {showPlaceholder && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
            <div className="w-32 h-32 md:w-48 md:h-48 bg-purple-600/30 rounded-full flex items-center justify-center">
              <User className="w-16 h-16 md:w-24 md:h-24 text-purple-400" />
            </div>
          </div>
        )}
        {emojiBadge}
        {micBadge}
        <div className="absolute bottom-6 left-6 flex items-center space-x-2 z-20">
          <div className="bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg flex items-center space-x-2">
            {participant.isScreenSharing && <Monitor className="w-4 h-4 text-green-400" />}
            {nameLabel}
          </div>
        </div>
      </div>
    );
  }

  if (size === 'large') {
    return (
      <div className={`relative ${bgClass} w-full h-full overflow-hidden`} {...recordingTileProps}>
        {videoLayer}
        {showPlaceholder && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
            <div className="w-24 h-24 bg-purple-600/30 rounded-full flex items-center justify-center">
              <User className="w-12 h-12 text-purple-400" />
            </div>
          </div>
        )}
        {emojiBadge}
        {micBadge}
        <div className="absolute bottom-4 left-4 z-20">
          <div className="bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-lg">
            {nameLabel}
          </div>
        </div>
      </div>
    );
  }

  if (size === 'small') {
    return (
      <div
        {...recordingTileProps}
        className={`relative ${bgClass} rounded-lg overflow-hidden cursor-pointer transition-all duration-200 aspect-video w-full ${
          isSelected ? 'ring-2 ring-purple-500' : 'hover:ring-1 hover:ring-purple-500/50'
        } ${participant.handRaised ? 'ring-2 ring-amber-500/60' : ''}`}
        onClick={onClick}
      >
        {videoLayer}
        {showPlaceholder && (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-900/50 to-slate-800">
            <div className="w-10 h-10 bg-purple-600/30 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-purple-400" />
            </div>
          </div>
        )}
        {emojiBadge}
        {micBadge}
        <div className="absolute bottom-1 left-1 right-1 z-20">
          <div className="bg-black/70 backdrop-blur-sm px-1.5 py-0.5 rounded text-center">
            <span className="text-xs truncate block">{nameLabel}</span>
          </div>
        </div>
        {participant.isScreenSharing && !showCameraInsteadOfScreen && (
          <div className="absolute top-1 left-1 bg-green-600/80 p-0.5 rounded z-20">
            <Monitor className="w-2.5 h-2.5 text-white" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      {...recordingTileProps}
      className={`relative ${bgClass} rounded-xl overflow-hidden cursor-pointer hover:ring-2 hover:ring-purple-500 transition-all duration-200 ${frameClass} ${
        participant.handRaised ? 'ring-2 ring-amber-500/50' : ''
      }`}
      onClick={onClick}
    >
      {videoLayer}
      {showPlaceholder && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-900/50 to-slate-800">
          <div className="w-16 h-16 md:w-24 md:h-24 bg-purple-600/30 rounded-full flex items-center justify-center">
            <User className="w-8 h-8 md:w-12 md:h-12 text-purple-400" />
          </div>
        </div>
      )}
      {emojiBadge}
      {micBadge}
      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between z-30 pointer-events-none">
        <div className="bg-black/70 backdrop-blur-sm px-2 py-1 rounded-lg max-w-[75%] text-xs md:text-sm text-white truncate">
          {nameLabel}
        </div>
        {participant.isScreenSharing && (
          <div className="bg-green-600/80 backdrop-blur-sm p-1 rounded-lg animate-pulse">
            <Monitor className="w-3 h-3 md:w-4 md:h-4 text-white" />
          </div>
        )}
      </div>
      {isSelected && (
        <div className="absolute inset-0 ring-2 ring-purple-500 rounded-xl pointer-events-none" />
      )}
    </div>
  );
};

export default VideoTile;



